import React, { useMemo } from 'react';
import type { ControllerFieldState, ControllerRenderProps, FieldPath } from 'react-hook-form';
import { TextField, type TextFieldProps } from '@mui/material';

import type { CMS1500FormData } from '../ClaimCMS1500Form';
import { combineSx } from '../ClaimCMS1500Form.styles';
import { useFieldErrorStyling } from '../hooks/useFieldErrorStyling';

interface ClaimTextFieldProps<TFieldName extends FieldPath<CMS1500FormData> = FieldPath<CMS1500FormData>> 
  extends Omit<TextFieldProps, 'error' | 'helperText'> {
  /** Field props from react-hook-form Controller */
  field: ControllerRenderProps<CMS1500FormData, TFieldName>;
  /** Field state from react-hook-form Controller */
  fieldState?: ControllerFieldState;
  /** Override helper text (defaults to error message) */
  helperText?: string;
}

/**
 * ClaimTextField - A TextField wrapper that automatically applies severity-based error styling
 * 
 * This component handles:
 * - Error severity detection (error/warning/info)
 * - Appropriate border colors and widths
 * - Helper text color coding
 * - Consistent styling across all claim form fields
 * 
 * @example
 * ```tsx
 * <Controller
 *   name="patientName"
 *   control={control}
 *   render={({ field, fieldState }) => (
 *     <ClaimTextField
 *       field={field}
 *       fieldState={fieldState}
 *       size="small"
 *       fullWidth
 *       placeholder="Patient Name"
 *     />
 *   )}
 * />
 * ```
 */
export const ClaimTextField = <TFieldName extends FieldPath<CMS1500FormData> = FieldPath<CMS1500FormData>>({
  field,
  fieldState,
  helperText,
  sx,
  ...textFieldProps
}: ClaimTextFieldProps<TFieldName>): React.ReactElement => {
  const errorStyling = useFieldErrorStyling(fieldState);

  // Combine sx props, ensuring we don't pass arrays to TextField
  const combinedSx = useMemo(() => {
    // Check if we have error styling (exclude null/undefined)
    const hasErrorStyling = Boolean(errorStyling.sx) && 
      errorStyling.sx !== null &&
      typeof errorStyling.sx === 'object' && 
      !Array.isArray(errorStyling.sx) &&
      Object.keys(errorStyling.sx as object).length > 0;
    
    // Check if we have custom sx
    const hasCustomSx = Boolean(sx);
    
    if (!hasErrorStyling && !hasCustomSx) {
      return undefined;
    }
    
    if (hasErrorStyling && hasCustomSx && sx) {
      // Combine the styles using MUI's built-in support for arrays
      return combineSx(errorStyling.sx, sx);
    }
    
    if (hasErrorStyling) return errorStyling.sx;
    if (hasCustomSx) return sx;
    
    return undefined;
  }, [errorStyling.sx, sx]);

  return (
    <TextField
      {...field}
      {...textFieldProps}
      error={errorStyling.error}
      helperText={helperText ?? fieldState?.error?.message}
      sx={combinedSx}
    />
  );
};

ClaimTextField.displayName = 'ClaimTextField';